import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "../../lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

/**
 * Default for the `portalled` flag, supplied by an ancestor. Lets a parent
 * container (e.g. a modal Dialog whose body-level pointer-events lock would
 * otherwise swallow clicks on a portalled popover) flip the default for every
 * `<PopoverContent>` rendered inside it — without prop-drilling through
 * intermediate components. Explicit `portalled` props still win over the
 * context.
 */
const PopoverPortalDefaultContext = React.createContext<boolean | undefined>(
  undefined,
);

/**
 * Wrap a subtree to set the default `portalled` flag for all `<PopoverContent>`
 * descendants. Mount this inside a modal Dialog with `portalled={false}` so
 * popovers inside the dialog render inline (and receive cursor / click events
 * even though the Dialog has locked `pointer-events: none` on `<body>`).
 */
function PopoverPortalDefaultProvider({
  portalled,
  children,
}: {
  portalled: boolean;
  children: React.ReactNode;
}) {
  return (
    <PopoverPortalDefaultContext.Provider value={portalled}>
      {children}
    </PopoverPortalDefaultContext.Provider>
  );
}

type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> & {
  /**
   * When `true` (default), the content is rendered into a body-level portal
   * via Radix's `<Portal>`. Set to `false` to render the content inline as a
   * descendant of the trigger's DOM ancestor — required when the host is a
   * modal Radix Dialog that locks `pointer-events: none` on `<body>`, since
   * body-level portals would otherwise inherit the lock and silently
   * swallow clicks. An ancestor `PopoverPortalDefaultProvider` can flip this
   * default without prop-drilling.
   */
  portalled?: boolean;
};

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = "center", sideOffset = 4, portalled, ...props }, ref) => {
  const contextDefault = React.useContext(PopoverPortalDefaultContext);
  const resolvedPortalled = portalled ?? contextDefault ?? true;
  const content = (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        // Entrance: instant-feeling fade only (no zoom/slide), ≤100ms —
        // native feel. Exit keeps the soft default fade+zoom (CH-19).
        "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none [&[data-state=open]]:animate-in [&[data-state=closed]]:animate-out [&[data-state=closed]]:fade-out-0 [&[data-state=open]]:fade-in-0 [&[data-state=closed]]:zoom-out-95 [&[data-state=open]]:duration-100",
        className
      )}
      {...props}
    />
  );
  return resolvedPortalled ? (
    <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>
  ) : (
    content
  );
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  PopoverPortalDefaultProvider,
}
